/**
 * Calculator pages opened from the AI Design Assistant open in a NEW browser tab, so the
 * engineer never leaves the conversation (Andrew, 2026-10-15).
 *
 * A browser lets a page open a tab only while a click is fresh, and Open in Calculator
 * first creates or updates the calculation (several requests). So the click opens an empty
 * tab at once (`openPendingTab`) and the page address is sent to it when the calculation is
 * ready (`sendToTab`); a flow that ends without a page closes it (`closeTab`). The tab is
 * cut from this page (`opener = null`), as `rel="noopener"` would.
 *
 * The page carries `aiSession=<session id>` so the new tab syncs the engineer's edits to
 * the chat session, as the calculator did when it opened in this tab (StateSyncService).
 * Pure TypeScript (no Angular) so it is unit-testable under plain Node.
 */

/** The window functions used here: the browser's `window`, or a test double. */
export interface TabHost {
  open(url?: string, target?: string, features?: string): Window | null;
}

/** The query parameter naming the chat session a calculator page syncs its edits to. */
export const AI_SESSION_PARAM = 'aiSession';

/** Open an empty tab now, while the click is fresh; null when the browser refused. */
export function openPendingTab(host: TabHost): Window | null {
  const tab = host.open('', '_blank');
  if (tab) {
    try { tab.opener = null; } catch { /* a browser that will not let it go keeps it */ }
  }
  return tab;
}

/** Show `url` in the pending tab, or in a new one when there is none (or it was closed). */
export function sendToTab(tab: Window | null, url: string, host: TabHost): void {
  if (tab && !tab.closed) {
    tab.location.href = url;
  } else {
    host.open(url, '_blank', 'noopener');
  }
}

/** Close a pending tab the flow did not use. */
export function closeTab(tab: Window | null): void {
  if (tab && !tab.closed) {
    try { tab.close(); } catch { /* already gone */ }
  }
}

/** A calculator page's address: `/app/main/projects/{project}/{route}/{calc}?...`. */
export function calcPageUrl(
  projectId: number, routeSegment: string, calcId: number,
  query: Record<string, string | null | undefined> = {},
): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v) q.set(k, v);
  }
  const qs = q.toString();
  return `/app/main/projects/${projectId}/${routeSegment}/${calcId}${qs ? '?' + qs : ''}`;
}

/** The chat session a calculator page was opened for (its `aiSession`), or null. */
export function aiSessionOf(search: string): string | null {
  const sid = new URLSearchParams(search).get(AI_SESSION_PARAM);
  return sid && /^[A-Za-z0-9-]{8,64}$/.test(sid) ? sid : null;
}
